import { useMemo } from 'react';

import { useExternalAccessRights } from 'common/hooks';
import { usePermissions } from 'common/wolt-permissions';
import { hasMenuEditorPermission, hasPayoutReportsPermission } from 'common/wolt-permissions/permissionUtils';
import { useIsVenueGroupEnabled } from 'modules/brand/hooks/useIsVenueGroupEnabled';
import { useCanViewCampaigns, useExperienceModuleContext } from 'modules/experience-core';
import { ExperienceEntityType, NavigationItem } from 'modules/experience-core';
import { useCanViewInsights, useCanViewUserManagement } from 'modules/experience-core';
import { useIsWoltCapitalEnabled } from 'modules/wolt-capital';

import {
  ACCESS_MANAGEMENT_NAVIGATION_ITEM,
  BRAND_VENUE_GROUPS_NAVIGATION_ITEM,
  INSIGHTS_NAVIGATION_ITEM,
  LISTING_MANAGER_ITEM,
  MERCHANT_CAMPAIGNS_NAVIGATION_ITEM,
  PAYOUT_REPORTS_ITEM,
  PURCHASE_DATA_NAVIGATION_ITEM,
  VENUES_NAVIGATION_ITEM,
  WOLT_CAPITAL_ITEM,
} from '../constants';

export const useBusinessNavigationItems = (): NavigationItem[] => {
  const { experienceEntity, country } = useExperienceModuleContext();
  const { experienceEntityType, experienceEntityId, experienceEntityObject } = experienceEntity;
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();
  const isWoltCapitalEnabled = useIsWoltCapitalEnabled(
    experienceEntityId,
    experienceEntityType === ExperienceEntityType.MERCHANT,
  );

  const shouldShowInsightsNavigation = useCanViewInsights(experienceEntity);
  const shouldShowAccessManagementNavigation = useCanViewUserManagement(experienceEntity);
  const shouldShowCampaignNavigation =
    useCanViewCampaigns(experienceEntity, []) &&
    experienceEntity.experienceEntityType === ExperienceEntityType.MERCHANT;

  const shouldShowBrandVenueGroupsNavigation = useIsVenueGroupEnabled(
    country,
    experienceEntityId,
    experienceEntityType === ExperienceEntityType.BRAND,
  );
  const shouldShowVenuesNavigation = experienceEntityType !== ExperienceEntityType.VENUE;
  const shouldShowListingManagerNavigation =
    experienceEntityType === ExperienceEntityType.VENUE
      ? hasMenuEditorPermission(permissions, externalAccessRights, experienceEntityObject) &&
        experienceEntityObject.type !== 'daas'
      : true;
  const shouldShowPayoutReportsNavigation =
    experienceEntityType === ExperienceEntityType.VENUE
      ? hasPayoutReportsPermission(permissions, externalAccessRights, experienceEntityObject) &&
        experienceEntityObject.type !== 'daas'
      : true;
  const shouldShowWoltCapitalNavigation =
    experienceEntityType === ExperienceEntityType.MERCHANT && isWoltCapitalEnabled;
  const shouldShowPurchaseDataNavigation = experienceEntityType !== ExperienceEntityType.VENUE;
  // End TODO: Implement logic to determine if navigation should be shown

  return useMemo(
    () => [
      ...(shouldShowInsightsNavigation ? [INSIGHTS_NAVIGATION_ITEM] : []),
      ...(shouldShowAccessManagementNavigation ? [ACCESS_MANAGEMENT_NAVIGATION_ITEM] : []),
      ...(shouldShowBrandVenueGroupsNavigation ? [BRAND_VENUE_GROUPS_NAVIGATION_ITEM] : []),
      ...(shouldShowCampaignNavigation ? [MERCHANT_CAMPAIGNS_NAVIGATION_ITEM] : []),
      ...(shouldShowPurchaseDataNavigation ? [PURCHASE_DATA_NAVIGATION_ITEM] : []),
      ...(shouldShowVenuesNavigation ? [VENUES_NAVIGATION_ITEM] : []),
      ...(shouldShowListingManagerNavigation ? [LISTING_MANAGER_ITEM] : []),
      ...(shouldShowPayoutReportsNavigation ? [PAYOUT_REPORTS_ITEM] : []),
      ...(shouldShowWoltCapitalNavigation ? [WOLT_CAPITAL_ITEM] : []),
    ],
    [
      shouldShowInsightsNavigation,
      shouldShowAccessManagementNavigation,
      shouldShowBrandVenueGroupsNavigation,
      shouldShowCampaignNavigation,
      shouldShowVenuesNavigation,
      shouldShowPurchaseDataNavigation,
      shouldShowListingManagerNavigation,
      shouldShowPayoutReportsNavigation,
      shouldShowWoltCapitalNavigation,
    ],
  );
};
